import React from "react";
import { useSelector } from "react-redux";
import { NMStatsTable, BarChart } from "@neumetric/blueprint-lib/components";
import { getMitigatorDashboardStatsData } from "../../../store";
import { ExportSection } from "../ExportSection";
import ExportAxisLabel from "../ExportAxisLabel";

const SeverityColumns = [
  { typeRepresentation: "DOT", title: "", attribute: "status", width: "17%" },
  {
    title: "OVERALL",
    id: "Overall",
    attribute: "total",
    width: "11%",
    highlight: { header: true, cell: true },
    typeRepresentation: "DOT",
    overallFlag: true,
    align: "center-right",
  },
  {
    title: "Emergency",
    id: "emergency",
    attribute: "emergency",
    width: "13%",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Urgent",
    id: "urgent",
    attribute: "urgent",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Critical",
    id: "critical",
    attribute: "critical",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "High",
    id: "high",
    attribute: "high",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Medium",
    id: "medium",
    attribute: "medium",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Low",
    id: "low",
    attribute: "low",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Minimal",
    id: "minimal",
    attribute: "minimal",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "None",
    id: "INVALID",
    attribute: "other",
    typeRepresentation: "DOT",
    align: "center-right",
  },
];

const SEVERITY_ORDER = [
  "None",
  "Minimal",
  "Low",
  "Medium",
  "High",
  "Critical",
  "Urgent",
  "Emergency",
];

const STATUS_ORDER = [
  "Pending",
  "Accepted",
  "Partial",
  "Mitigated",
  "Tolerated",
  "Redundant",
  "Inapplicable",
];

const STATUS_DOT_ID = {
  Pending: "pending",
  Accepted: "closed",
  Partial: "started",
  Mitigated: "complete",
  Tolerated: "urgent",
  Redundant: "redundant",
  Inapplicable: "abandoned",
};
const EXPORT_SEVERITY_KEYS = [
  "emergency",
  "urgent",
  "critical",
  "high",
  "medium",
  "low",
  "minimal",
  "other",
];

// --- Helpers ---
const parseKey = (k) => {
  if (typeof k !== "string") return { a: "", b: null };
  const safe = k.replace(/\bnil\b/g, "null");
  try {
    const arr = JSON.parse(safe);
    return { a: arr?.[0], b: arr?.[1] };
  } catch {
    return { a: "", b: null };
  }
};

const normalizeSeverityAttr = (sev) => {
  const s = (sev || "").toString().trim().toLowerCase();
  if (["critical", "high", "medium", "low", "minimal"].includes(s)) return s;
  return "other";
};

const normalizeStatusLabel = (raw) => {
  if (raw == null || raw === "") return "Inapplicable";
  const s = raw.toString().trim().toLowerCase();

  const MAP = {
    pending: "Pending",
    accepted: "Accepted",
    partial: "Partial",
    mitigated: "Mitigated",
    tolerated: "Tolerated",
    redundant: "Redundant",
    inapplicable: "Inapplicable",

    open: "Pending",
    draft: "Pending",

    closed: "Accepted",
    active: "Accepted",
    started: "Partial",
    complete: "Mitigated",
    urgent: "Tolerated",

    archived: "Redundant",
    inactive: "Redundant",
    none: "Inapplicable",
  };

  return MAP[s] || "Inapplicable";
};

const emptyRow = (statusLabel) => ({
  status: { id: STATUS_DOT_ID[statusLabel] || "INVALID", label: statusLabel },
  total: 0,
  critical: 0,
  high: 0,
  medium: 0,
  low: 0,
  minimal: 0,
  other: 0,
});

export const StatusVsSeverity = ({ user }) => {
  const mitigatorDashboardData = useSelector(getMitigatorDashboardStatsData);
  const stats = mitigatorDashboardData?.status_vs_severity || {};

  const companyLabel = user?.org?.brandname ?? "Luciance";

  const dashZero = (v) => {
    const n = Number(v) || 0;
    return n === 0 ? "-" : n;
  };

  const toTableRow = (r) => ({
    ...r,
    total: dashZero(r.total),
    critical: dashZero(r.critical),
    high: dashZero(r.high),
    medium: dashZero(r.medium),
    low: dashZero(r.low),
    minimal: dashZero(r.minimal),
    other: dashZero(r.other),
  });

  const { tableRows, datasets } = React.useMemo(() => {
    const rowsByStatus = {};
    STATUS_ORDER.forEach((lbl) => (rowsByStatus[lbl] = emptyRow(lbl)));

    Object.entries(stats || {}).forEach(([k, count]) => {
      const { a: rawStatus, b: rawSeverity } = parseKey(k);

      const statusLabel = normalizeStatusLabel(rawStatus);
      const severityAttr = normalizeSeverityAttr(rawSeverity);
      const n = Number(count) || 0;

      if (!rowsByStatus[statusLabel])
        rowsByStatus[statusLabel] = emptyRow(statusLabel);

      const targetKey =
        rowsByStatus[statusLabel][severityAttr] === undefined
          ? "other"
          : severityAttr;

      rowsByStatus[statusLabel][targetKey] += n;
      rowsByStatus[statusLabel].total += n;
    });

    const numericRows = STATUS_ORDER.map((s) => rowsByStatus[s] || emptyRow(s));

    const totalRowNumeric = numericRows.reduce(
      (acc, r) => ({
        ...acc,
        total: acc.total + (Number(r.total) || 0),
        critical: acc.critical + (Number(r.critical) || 0),
        high: acc.high + (Number(r.high) || 0),
        medium: acc.medium + (Number(r.medium) || 0),
        low: acc.low + (Number(r.low) || 0),
        minimal: acc.minimal + (Number(r.minimal) || 0),
        other: acc.other + (Number(r.other) || 0),
      }),
      {
        ...emptyRow(companyLabel),
        status: { id: "Overall", label: companyLabel },
      },
    );

    const hasAnyData = (Number(totalRowNumeric.total) || 0) > 0;

    const tableRows = hasAnyData
      ? [
          {
            ...toTableRow(totalRowNumeric),
            status: { id: "Overall", label: companyLabel },
            highlight: true,
          },
          ...numericRows.map(toTableRow),
        ]
      : [];

    const datasets = {};
    STATUS_ORDER.forEach((statusLabel) => {
      const r = rowsByStatus[statusLabel] || emptyRow(statusLabel);
      datasets[statusLabel] = SEVERITY_ORDER.map((sevLabel) => {
        const attr = sevLabel === "None" ? "other" : sevLabel.toLowerCase();
        return {
          key: attr === "other" ? "INVALID" : attr,
          label: sevLabel,
          value: Number(r[attr] || 0),
        };
      });
    });

    return { tableRows, datasets };
  }, [stats, companyLabel]);

  const exportRowsAsCsv = React.useMemo(() => {
    return (tableRows || []).map((row) => {
      const flatRow = {
        status: row?.status?.label ?? "-",
        total: row?.total ?? "-",
      };

      EXPORT_SEVERITY_KEYS.forEach((key) => {
        flatRow[key] =
          row[key] === undefined || row[key] === null ? "-" : row[key];
      });

      if (row.highlight) {
        flatRow.highlight = true;
      }

      return flatRow;
    });
  }, [tableRows]);

  const csvSeverityColumns = React.useMemo(() => {
    if (!Array.isArray(SeverityColumns) || SeverityColumns.length === 0)
      return [];

    return SeverityColumns.map((col, idx) => {
      if (idx !== 0) return col;

      return {
        ...col,
        title: "Risks (Status vs Severity)", // CSV header only
      };
    });
  }, [SeverityColumns]);

  return (
    <div className="d-flex gap-20 p20">
      <ExportSection
        index={1}
        moduleKey={"Mitigator_Status_vs_Severity_Status"}
        exportType="table"
        exportData={{
          title: "Risks - Status vs Severity",
          columns: csvSeverityColumns,
          rows: exportRowsAsCsv,
        }}
        style={{ flex: "1 1 55%", maxWidth: "60%" }}
      >
        <NMStatsTable
          title="Risks"
          subTitle="Status vs Severity"
          columns={SeverityColumns}
          dataSource={tableRows}
          getCellTypeRepresentation={({ rowId, columnId }) => {
            if (rowId !== -1 && columnId === "status") return "DOT";
            return undefined;
          }}
        />
      </ExportSection>
      <ExportSection
        index={2}
        moduleKey={"Mitigator_Status_vs_Severity_Status"}
        style={{ flex: "1 1 45%", maxWidth: "50%", position: "relative" }}
      >
        <ExportAxisLabel text="Status" />

        <BarChart
          title="Risks"
          subtitle="Status vs Severity"
          label={{ xAxis: "Risks", yAxis: "" }}
          datasets={datasets}
          stackType="normal"
          classes={{ root: "h-575-px mt-12" }}
        />
      </ExportSection>
    </div>
  );
};
